import React, { useState, useEffect, useCallback } from 'react';
import { Printer, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DIYOrdersTab({ auth }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | pending | sent | cancelled
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/diy-orders`, { headers: { 'Authorization': `Basic ${auth}` } });
      if (res.ok) setOrders(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const filtered = orders.filter(o => filter === 'all' || o.status === filter);

  const counts = {
    all: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    sent: orders.filter(o => o.status === 'sent').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
  };

  const updateStatus = async (id, patch) => {
    try {
      const res = await fetch(`${API}/admin/diy-orders/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) load();
    } catch { /* ignore */ }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm('Slette denne bestillingen?')) return;
    try {
      const res = await fetch(`${API}/admin/diy-orders/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${auth}` },
      });
      if (res.ok) load();
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="diy-orders-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-xl sm:text-2xl font-semibold text-stone-900">DIY-bestillinger fra nettsiden</h2>
        <button onClick={load} className="text-sm text-emerald-800 hover:text-emerald-900" data-testid="diy-refresh-btn">Oppdater</button>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto">
        {[
          { id: 'all', label: 'Alle' },
          { id: 'pending', label: 'Nye' },
          { id: 'sent', label: 'Sendt' },
          { id: 'cancelled', label: 'Kansellert' },
        ].map(t => (
          <button key={t.id} onClick={() => setFilter(t.id)} className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${filter === t.id ? 'bg-emerald-800 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`} data-testid={`diy-filter-${t.id}`}>
            {t.label} ({counts[t.id]})
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-stone-500">Laster...</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-stone-500">
          Ingen bestillinger {filter !== 'all' ? `med status "${filter}"` : 'enda'}.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(o => (
            <DIYOrderCard
              key={o.id}
              order={o}
              auth={auth}
              onMarkSent={() => updateStatus(o.id, { status: 'sent' })}
              onMarkPaid={() => updateStatus(o.id, { payment_status: 'paid' })}
              onMarkUnpaid={() => updateStatus(o.id, { payment_status: 'unpaid' })}
              onCancel={() => updateStatus(o.id, { status: 'cancelled' })}
              onReopen={() => updateStatus(o.id, { status: 'pending' })}
              onDelete={() => deleteOrder(o.id)}
              onEdit={() => setEditing(o)}
            />
          ))}
        </div>
      )}

      {editing && (
        <DIYOrderEditModal
          order={editing}
          auth={auth}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function DIYOrderCard({ order, auth, onMarkSent, onMarkPaid, onMarkUnpaid, onCancel, onReopen, onDelete, onEdit }) {
  const statusColor = order.status === 'sent' ? 'bg-emerald-100 text-emerald-800' : order.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800';
  const statusLabel = order.status === 'sent' ? 'Sendt' : order.status === 'cancelled' ? 'Kansellert' : 'Ny';
  const payColor = order.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-200 text-stone-700';
  const hasNumbers = (order.warranty_numbers || []).length > 0;

  const printLabels = async () => {
    try {
      const res = await fetch(`${API}/admin/diy-orders/${order.id}/labels`, { headers: { 'Authorization': `Basic ${auth}` } });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        alert(e.detail || 'Kunne ikke generere etiketter.');
        return;
      }
      const data = await res.json();
      const labels = data.labels || [];
      const win = window.open('', '_blank');
      win.document.write(`<!DOCTYPE html><html><head><title>DIY-etiketter — ${data.customer_name}</title>
        <style>
          @page { size: A4; margin: 12mm; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 16px; }
          .header { background: #f5f5f4; border-left: 4px solid #064e3b; padding: 10px 16px; border-radius: 6px; margin-bottom: 14mm; }
          .header h1 { margin: 0 0 4px 0; font-size: 18px; color: #064e3b; }
          .header p { margin: 0; color: #57534e; font-size: 13px; }
          .label-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8mm; }
          .label { border: 2px solid #064e3b; padding: 8mm; text-align: center; border-radius: 6px; page-break-inside: avoid; }
          .label .qr { display: block; margin: 0 auto 4mm auto; width: 50mm; height: 50mm; }
          .label .number { font-size: 16px; font-weight: bold; letter-spacing: 1px; color: #064e3b; }
          .label .type { font-size: 11px; color: #78716c; margin-top: 2mm; }
          .label .hint { font-size: 10px; color: #78716c; margin-top: 3mm; line-height: 1.3; }
          @media print { .no-print { display: none; } }
        </style></head><body>
        <button class="no-print" onclick="window.print()" style="position:fixed;top:8px;right:8px;padding:8px 18px;background:#064e3b;color:white;border:none;border-radius:8px;font-size:14px;cursor:pointer;">Skriv ut</button>
        <div class="header">
          <h1>DIY-etiketter til kit (${labels.length} stk)</h1>
          <p>Kunde: ${data.customer_name}</p>
        </div>
        <div class="label-grid">
          ${labels.map(l => `
            <div class="label">
              <img class="qr" src="${l.qr_data_url}" alt="QR ${l.warranty_number}" />
              <div class="number">${l.warranty_number}</div>
              <div class="type">10 års garanti — DC-Protect</div>
              <div class="hint">Skann QR-koden etter påføring, eller gå til<br/>gravsteinoppfriskeren.no/registrer-garanti</div>
            </div>
          `).join('')}
        </div>
      </body></html>`);
      win.document.close();
    } catch {
      alert('Kunne ikke generere etiketter.');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow border border-stone-200 p-4 sm:p-5" data-testid={`diy-order-${order.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`} data-testid={`diy-order-status-${order.id}`}>{statusLabel}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${payColor}`}>{order.payment_status === 'paid' ? 'Betalt' : 'Ubetalt'}</span>
            <span className="text-xs text-stone-400 font-mono">#{order.id.slice(0, 8).toUpperCase()}</span>
          </div>
          <h3 className="text-lg font-semibold text-stone-900">{order.customer_name}</h3>
          <p className="text-sm text-stone-600">{order.email} · {order.phone}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-emerald-800">{order.total_price} kr</div>
          <div className="text-xs text-stone-500">{order.quantity} kit · {order.shipping_fee} kr frakt</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 text-sm mb-3 bg-stone-50 rounded-lg p-3">
        <div>
          <div className="text-stone-500 text-xs uppercase">Leveringsadresse</div>
          <div className="text-stone-800">{order.shipping_address}<br/>{order.postal_code} {order.city}</div>
        </div>
        <div>
          <div className="text-stone-500 text-xs uppercase">Bestilt</div>
          <div className="text-stone-800">{new Date(order.created_date).toLocaleString('nb-NO')}</div>
          {order.sent_date && (
            <div className="text-emerald-700 text-xs mt-1">Sendt: {new Date(order.sent_date).toLocaleDateString('nb-NO')}</div>
          )}
        </div>
      </div>

      {order.notes && (
        <div className="text-sm bg-amber-50 border-l-4 border-amber-400 px-3 py-2 mb-3 text-stone-700">
          <strong>Kundenotat:</strong> {order.notes}
        </div>
      )}

      {order.warranty_numbers && order.warranty_numbers.length > 0 && (
        <div className="text-sm mb-3">
          <span className="text-stone-500 text-xs uppercase">Tilordnede garantinumre:</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {order.warranty_numbers.map(n => (
              <span key={n} className="font-mono text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded">{n}</span>
            ))}
          </div>
        </div>
      )}

      {order.admin_note && (
        <div className="text-sm text-stone-600 italic mb-3">Internt notat: {order.admin_note}</div>
      )}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-100">
        {order.status !== 'sent' && (
          <button onClick={onMarkSent} className="text-sm bg-emerald-800 text-white hover:bg-emerald-900 px-3 py-1.5 rounded-lg" data-testid={`diy-mark-sent-${order.id}`}>Marker som sendt</button>
        )}
        {order.payment_status === 'unpaid' ? (
          <button onClick={onMarkPaid} className="text-sm bg-stone-900 text-white hover:bg-stone-800 px-3 py-1.5 rounded-lg" data-testid={`diy-mark-paid-${order.id}`}>Marker betalt</button>
        ) : (
          <button onClick={onMarkUnpaid} className="text-sm bg-stone-200 text-stone-700 hover:bg-stone-300 px-3 py-1.5 rounded-lg">Angre betaling</button>
        )}
        <button onClick={onEdit} className="text-sm bg-stone-100 text-stone-700 hover:bg-stone-200 px-3 py-1.5 rounded-lg" data-testid={`diy-edit-${order.id}`}>Rediger / tilordne kit</button>
        {hasNumbers && (
          <button onClick={printLabels} className="text-sm bg-stone-900 text-white hover:bg-stone-800 px-3 py-1.5 rounded-lg flex items-center gap-1" data-testid={`diy-print-labels-${order.id}`}>
            <Printer size={14} /> Skriv ut etiketter
          </button>
        )}
        {order.status !== 'cancelled' ? (
          <button onClick={onCancel} className="text-sm text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg">Kanseller</button>
        ) : (
          <button onClick={onReopen} className="text-sm text-stone-600 hover:bg-stone-100 px-3 py-1.5 rounded-lg">Gjenåpne</button>
        )}
        <button onClick={onDelete} className="text-sm text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg ml-auto">Slett</button>
      </div>
    </div>
  );
}

function DIYOrderEditModal({ order, auth, onClose, onSaved }) {
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState(order.warranty_numbers || []);
  const [adminNote, setAdminNote] = useState(order.admin_note || '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/admin/diy-orders/available-warranties`, { headers: { 'Authorization': `Basic ${auth}` } });
        if (res.ok) {
          const data = await res.json();
          // include any already-assigned numbers in the list so they appear selected
          const set = new Set([...(data.available || []), ...(order.warranty_numbers || [])]);
          setAvailable(Array.from(set).sort());
        } else {
          setError('Kunne ikke hente tilgjengelige DIY-garantinumre.');
        }
      } catch { setError('Tilkobling feilet.'); }
      setLoading(false);
    })();
  }, [auth, order.warranty_numbers]);

  const toggle = (n) => {
    setSelected(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/admin/diy-orders/${order.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ warranty_numbers: selected, admin_note: adminNote }),
      });
      if (res.ok) onSaved();
      else {
        const e = await res.json().catch(() => ({}));
        setError(e.detail || 'Kunne ikke lagre.');
      }
    } catch { setError('Tilkobling feilet.'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="diy-edit-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 bg-emerald-900 text-white">
          <h3 className="font-semibold">Tilordne garantinumre — {order.customer_name}</h3>
          <button onClick={onClose} className="hover:bg-emerald-800 rounded-full p-1" data-testid="diy-edit-close-btn"><X size={20} /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          <p className="text-sm text-stone-600 mb-3">Velg <strong>{order.quantity}</strong> DIY-garantinummer som skal pakkes i denne bestillingen. Numrene vises på QR-etikettene som legges i kittene.</p>
          {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
          {loading ? (
            <p className="text-stone-500">Laster tilgjengelige numre...</p>
          ) : available.length === 0 ? (
            <p className="text-stone-500">Ingen ledige DIY-garantinumre. Generer flere DIY-kit i fanen "+ Nytt kit".</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-80 overflow-y-auto">
              {available.map(n => (
                <label key={n} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${selected.includes(n) ? 'bg-emerald-50 border-emerald-400' : 'bg-white border-stone-200 hover:bg-stone-50'}`}>
                  <input type="checkbox" checked={selected.includes(n)} onChange={() => toggle(n)} data-testid={`diy-wn-checkbox-${n}`} />
                  <span className="font-mono text-xs">{n}</span>
                </label>
              ))}
            </div>
          )}

          <div className="mt-4">
            <label className="block text-sm font-medium text-stone-700 mb-2">Internt notat (valgfritt)</label>
            <textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} rows={3} className="w-full px-3 py-2 border border-stone-300 rounded-lg" data-testid="diy-admin-note-input" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-stone-200 flex items-center justify-between">
          <span className="text-sm text-stone-600">Valgt: {selected.length} av {order.quantity}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-stone-700 hover:bg-stone-100">Avbryt</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-emerald-800 text-white hover:bg-emerald-900 disabled:opacity-60" data-testid="diy-save-btn">
              {saving ? 'Lagrer...' : 'Lagre'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// =====================  Marketing tab — printable / downloadable QR codes  =====================
